import { useState, useEffect } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { useTranslation } from "@/hooks/useTranslation";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { ImportResumeDialog } from "@/components/profile/ImportResumeDialog";
import { ImageCropDialog } from "@/components/ui/image-crop-dialog";
import { useTranslatedToast } from "@/hooks/useTranslatedToast";
import { supabase } from "@/integrations/supabase/client";
import { 
  Edit3, Camera, Save, X, MapPin, Globe, Phone, Mail, 
  Linkedin, Github, Twitter, Upload
} from "lucide-react";
import { buildDisplayName } from "@/lib/user-utils";

interface ProfileHeaderProps {
  profile: any;
  user: any;
  isEditing: boolean;
  onEditToggle: () => void;
  onProfileUpdate: () => void;
  readOnly?: boolean;
  /** Use nested=true when this component is rendered inside a dialog to ensure proper z-index for ImportResumeDialog */
  nested?: boolean;
  /** Hide the import resume button */
  hideImportResume?: boolean;
}

export const ProfileHeader = ({ 
  profile, 
  user, 
  isEditing, 
  onEditToggle, 
  onProfileUpdate,
  readOnly = false,
  nested = false,
  hideImportResume = false
}: ProfileHeaderProps) => {
  const { t } = useTranslation('ui');
  const toast = useTranslatedToast();
  const [saving, setSaving] = useState(false);
  const [editingSection, setEditingSection] = useState<string | null>(null);
  const [showImportDialog, setShowImportDialog] = useState(false);
  const [cropDialogOpen, setCropDialogOpen] = useState(false);
  const [selectedImageSrc, setSelectedImageSrc] = useState<string | null>(null);
  
  const [formData, setFormData] = useState({
    first_name: profile?.first_name || "",
    last_name: profile?.last_name || "",
    headline: profile?.headline || "",
    current_company: profile?.current_company || "",
    current_position: profile?.current_position || "",
    location: profile?.location || "",
    summary: profile?.summary || "",
    email: profile?.email || "",
    phone: profile?.phone || "",
    website: profile?.website || "",
    linkedin_url: profile?.linkedin_url || "",
    github_url: profile?.github_url || "",
    twitter_url: profile?.twitter_url || "",
  });

  // Update formData when profile changes
  useEffect(() => {
    if (profile) {
      setFormData({
        first_name: profile?.first_name || "",
        last_name: profile?.last_name || "",
        headline: profile?.headline || "",
        current_company: profile?.current_company || "",
        current_position: profile?.current_position || "",
        location: profile?.location || "",
        summary: profile?.summary || "",
        email: profile?.email || "",
        phone: profile?.phone || "",
        website: profile?.website || "",
        linkedin_url: profile?.linkedin_url || "",
        github_url: profile?.github_url || "",
        twitter_url: profile?.twitter_url || "",
      });
    }
  }, [profile]);

  const displayName = buildDisplayName({
    first_name: profile?.first_name,
    last_name: profile?.last_name,
    email: profile?.email || user?.email,
  });

  const handleImageUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    if (!event.target.files || !event.target.files[0]) return;
    
    const file = event.target.files[0];
    
    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast.error('error.invalid_file_type');
      return;
    }
    
    // Validate file size (max 10MB)
    if (file.size > 10 * 1024 * 1024) {
      toast.error('error.file_too_large');
      return;
    }
    
    // Create object URL for cropping
    const imageUrl = URL.createObjectURL(file);
    setSelectedImageSrc(imageUrl);
    setCropDialogOpen(true);
    
    // Reset file input
    event.target.value = '';
  };

  const handleCroppedImageUpload = async (croppedBlob: Blob) => {
    if (!user) return;
    
    try {
      setSaving(true);
      
      const fileExt = 'jpg';
      const fileName = `${user.id}/avatar.${fileExt}`;
      
      const { error: uploadError } = await supabase.storage
        .from('avatars')
        .upload(fileName, croppedBlob, { 
          upsert: true,
          contentType: 'image/jpeg'
        });
      
      if (uploadError) throw uploadError;
      
      const { data } = supabase.storage
        .from('avatars')
        .getPublicUrl(fileName);
      
      // Add cache-busting timestamp
      const avatarUrl = `${data.publicUrl}?t=${Date.now()}`;
      
      const { error: updateError } = await supabase
        .from('user_profiles')
        .upsert({ 
          user_id: user.id,
          avatar_url: avatarUrl
        } as any, { onConflict: 'user_id' });
      
      if (updateError) throw updateError;
      
      await onProfileUpdate();
      setCropDialogOpen(false);
      toast.success('profile.photo_uploaded');
    } catch (error) {
      console.error('Error uploading image:', error);
      toast.error('error.upload_failed');
    } finally {
      setSaving(false);
      if (selectedImageSrc) {
        URL.revokeObjectURL(selectedImageSrc);
        setSelectedImageSrc(null);
      }
    }
  };

  const handleSave = async () => {
    if (!user) return;
    
    setSaving(true);
    try {
      const { error } = await supabase
        .from('user_profiles')
        .upsert({
          user_id: user.id,
          ...formData,
          updated_at: new Date().toISOString()
        } as any, { onConflict: 'user_id' });

      if (error) throw error;

      await onProfileUpdate();
      setEditingSection(null);
      onEditToggle(); // Exit editing mode
      toast.success('profile.updated');
    } catch (error) {
      console.error('Error saving profile:', error);
      toast.error('error.failed_to_save');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card className="relative">
      {/* Remove gradient background */}
      
      {/* Edit Button - Hide when readOnly */}
      {!readOnly && (
        !isEditing ? (
          <div className="absolute top-4 right-4 flex gap-2 z-10">
            {!hideImportResume && (
              <>
                <ImportResumeDialog 
                  isOpen={showImportDialog} 
                  onClose={() => setShowImportDialog(false)} 
                  onSuccess={() => {
                    setShowImportDialog(false);
                    onProfileUpdate();
                  }} 
                />
                <Button
                  onClick={() => setShowImportDialog(true)}
                  variant="outline" 
                  size="sm"
                  className="bg-background/80 backdrop-blur-sm"
                >
                  <Upload className="w-4 h-4 mr-2" />
                  {t('profile_header.import_resume')}
                </Button>
              </>
            )}
            <Button
              onClick={onEditToggle}
              variant="outline" 
              size="sm"
              className="bg-background/80 backdrop-blur-sm"
            >
              <Edit3 className="w-4 h-4 mr-2" />
              {t('profile_header.edit')}
            </Button>
          </div>
        ) : (
          <div className="absolute top-4 right-4 flex gap-2 z-10">
            <Button onClick={onEditToggle} variant="outline" size="sm">
              <X className="w-4 h-4 mr-2" />
              {t('profile_header.cancel')}
            </Button>
            <Button onClick={handleSave} disabled={saving} size="sm">
              <Save className="w-4 h-4 mr-2" />
              {saving ? t('profile_header.loading') : t('profile_header.save')}
            </Button>
          </div>
        )
      )}

      <div className="px-6 py-6">
        {/* Add padding-top when editing to avoid button overlap */}
        <div className={`${isEditing ? 'pt-12' : ''}`}>
          {/* Avatar and Basic Info */}
          <div className="flex flex-col sm:flex-row gap-6">
            <div className="relative">
              <Avatar className="w-20 h-20 border-2 border-primary/10 shadow-lg">
                <AvatarImage src={profile?.avatar_url} alt={displayName} />
                <AvatarFallback className="text-xl bg-gradient-to-br from-primary/10 to-accent/5">
                  {displayName?.charAt(0) || 'U'}
                </AvatarFallback>
              </Avatar>
              {isEditing && !readOnly && (
                <Button
                  onClick={() => document.getElementById('avatar-upload')?.click()}
                  disabled={saving}
                  size="sm"
                  className="absolute bottom-0 right-0 h-8 w-8 rounded-full p-0 shadow-lg hover:shadow-xl transition-all duration-300"
                >
                  {saving ? (
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  ) : (
                    <Camera className="h-4 w-4" />
                  )}
                </Button>
              )}
              <input
                id="avatar-upload"
                type="file"
                accept="image/*"
                onChange={handleImageUpload}
                className="hidden"
              />
            </div>

            <div className="flex-1 min-w-0">
            {isEditing && !readOnly ? (
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-2">
                  <Input
                    value={formData.first_name}
                    onChange={(e) => setFormData(prev => ({ ...prev, first_name: e.target.value }))}
                    placeholder={t('profile_header.first_name_placeholder')}
                    className="text-lg font-semibold"
                  />
                  <Input
                    value={formData.last_name}
                    onChange={(e) => setFormData(prev => ({ ...prev, last_name: e.target.value }))}
                    placeholder={t('profile_header.last_name_placeholder')}
                    className="text-lg font-semibold"
                  />
                </div>
                <Input
                  value={formData.headline}
                  onChange={(e) => setFormData(prev => ({ ...prev, headline: e.target.value }))}
                  placeholder={t('profile_header.headline_placeholder')}
                />
                <div className="flex gap-2">
                  <Input
                    value={formData.current_position}
                    onChange={(e) => setFormData(prev => ({ ...prev, current_position: e.target.value }))}
                    placeholder={t('profile_header.current_position_placeholder')}
                  />
                  <Input
                    value={formData.current_company}
                    onChange={(e) => setFormData(prev => ({ ...prev, current_company: e.target.value }))}
                    placeholder={t('profile_header.current_company_placeholder')}
                  />
                </div>
                <Input
                  value={formData.location}
                  onChange={(e) => setFormData(prev => ({ ...prev, location: e.target.value }))}
                  placeholder={t('profile_header.location_placeholder')}
                />
              </div>
            ) : (
              <div className="space-y-2">
                <h1 className="text-2xl font-bold text-foreground">
                  {displayName}
                </h1>
                
                <p className="text-lg text-muted-foreground">
                  {profile?.headline || profile?.current_position || profile?.job_title || t('profile_header.add_headline')}
                </p>
              </div>
            )}
          </div>
        </div>

        {/* Profile Details as Tags - Always show this section */}
        {!isEditing && (
          <div className="flex items-center flex-wrap gap-2 text-xs mt-4">
            {(profile?.current_position || profile?.job_title) ? (
              <span className="flex items-center gap-1 px-2 py-1 rounded-full bg-muted text-muted-foreground">
                <div className="w-1.5 h-1.5 bg-muted-foreground rounded-full"></div>
                {profile?.current_position || profile?.job_title}
              </span>
            ) : (
              <span className="flex items-center gap-1 px-2 py-1 rounded-full bg-muted/50 text-muted-foreground/60">
                <div className="w-1.5 h-1.5 bg-muted-foreground/60 rounded-full"></div>
                {t('profile_header.no_position_specified')}
              </span>
            )}
            {profile?.current_company ? (
              <span className="flex items-center gap-1 px-2 py-1 rounded-full bg-muted text-muted-foreground">
                <div className="w-1.5 h-1.5 bg-muted-foreground rounded-full"></div>
                {profile.current_company}
              </span>
            ) : (
              <span className="flex items-center gap-1 px-2 py-1 rounded-full bg-muted/50 text-muted-foreground/60">
                <div className="w-1.5 h-1.5 bg-muted-foreground/60 rounded-full"></div>
                {t('profile_header.no_company_specified')}
              </span>
            )}
            {profile?.location ? (
              <span className="flex items-center gap-1 px-2 py-1 rounded-full bg-muted text-muted-foreground">
                <div className="w-1.5 h-1.5 bg-muted-foreground rounded-full"></div>
                {profile.location}
              </span>
            ) : (
              <span className="flex items-center gap-1 px-2 py-1 rounded-full bg-muted/50 text-muted-foreground/60">
                <div className="w-1.5 h-1.5 bg-muted-foreground/60 rounded-full"></div>
                {t('profile_header.no_location_specified')}
              </span>
            )}
          </div>
        )}

        {/* Summary - Always show this section */}
        {isEditing && !readOnly ? (
          <div className="mt-6 space-y-4">
            <div className="space-y-2">
              <h3 className="text-sm font-medium text-muted-foreground uppercase tracking-wide">{t('profile_header.about')}</h3>
              <Textarea
                value={formData.summary}
                onChange={(e) => setFormData(prev => ({ ...prev, summary: e.target.value }))}
                placeholder={t('profile_header.summary_placeholder')}
                rows={4}
              />
            </div>
          </div>
        ) : (
          <div className="mt-6 space-y-2">
            <h3 className="text-sm font-medium text-muted-foreground uppercase tracking-wide">{t('profile_header.about')}</h3>
            {profile?.summary ? (
              <p className="text-foreground leading-relaxed">
                {profile.summary}
              </p>
            ) : (
              <p className="text-muted-foreground/60 italic text-sm">
                {t('profile_header.no_summary_provided')}
              </p>
            )}
          </div>
        )}

        {/* Contact Information - Always show this section */}
        {isEditing && !readOnly ? (
          <div className="mt-6 space-y-4">
            <div className="space-y-2">
              <h3 className="text-sm font-medium text-muted-foreground uppercase tracking-wide">{t('profile_header.contact_information')}</h3>
              <div className="space-y-2">
                <label className="text-sm font-medium text-muted-foreground">
                  {t('profile_header.contact_email_label')}
                </label>
                <Input
                  type="email"
                  placeholder={t('profile_header.contact_email_placeholder')}
                  value={formData.email || ''}
                  onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                />
                <p className="text-xs text-muted-foreground">
                  {t('profile_header.contact_email_note')}
                </p>
              </div>
              <Input
                value={formData.phone}
                onChange={(e) => setFormData(prev => ({ ...prev, phone: e.target.value }))}
                placeholder={t('profile_header.phone_placeholder')}
              />
            </div>
          </div>
        ) : (
          <div className="mt-6 space-y-3">
            <h3 className="text-sm font-medium text-muted-foreground uppercase tracking-wide">{t('profile_header.contact_information')}</h3>
            <div className="flex flex-wrap gap-6">
              {profile?.email ? (
                <div className="flex items-center gap-2 text-sm">
                  <div className="h-4 w-4 rounded-full bg-muted-foreground/20 flex items-center justify-center">
                    <span className="text-xs">@</span>
                  </div>
                  <span>{profile.email}</span>
                </div>
              ) : (
                <div className="flex items-center gap-2 text-sm text-muted-foreground/60">
                  <div className="h-4 w-4 rounded-full bg-muted-foreground/10 flex items-center justify-center">
                    <span className="text-xs">@</span>
                  </div>
                  <span>{t('profile_header.no_email_provided')}</span>
                </div>
              )}
              {profile?.phone ? (
                <div className="flex items-center gap-2 text-sm">
                  <div className="h-4 w-4 rounded-full bg-muted-foreground/20 flex items-center justify-center">
                    <span className="text-xs">#</span>
                  </div>
                  <span>{profile.phone}</span>
                </div>
              ) : (
                <div className="flex items-center gap-2 text-sm text-muted-foreground/60">
                  <div className="h-4 w-4 rounded-full bg-muted-foreground/10 flex items-center justify-center">
                    <span className="text-xs">#</span>
                  </div>
                  <span>{t('profile_header.no_phone_provided')}</span>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Social Media Links - Always show this section */}
        {isEditing && !readOnly ? (
          <div className="mt-6 space-y-4">
            <div className="space-y-2">
              <h3 className="text-sm font-medium text-muted-foreground uppercase tracking-wide">{t('profile_header.social_media')}</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <Input
                  value={formData.website}
                  onChange={(e) => setFormData(prev => ({ ...prev, website: e.target.value }))}
                  placeholder={t('profile_header.website_placeholder')}
                />
                <Input
                  value={formData.linkedin_url}
                  onChange={(e) => setFormData(prev => ({ ...prev, linkedin_url: e.target.value }))}
                  placeholder={t('profile_header.linkedin_placeholder')}
                />
                <Input
                  value={formData.github_url}
                  onChange={(e) => setFormData(prev => ({ ...prev, github_url: e.target.value }))}
                  placeholder={t('profile_header.github_placeholder')}
                />
                <Input
                  value={formData.twitter_url}
                  onChange={(e) => setFormData(prev => ({ ...prev, twitter_url: e.target.value }))}
                  placeholder={t('profile_header.twitter_placeholder')}
                />
              </div>
            </div>
          </div>
        ) : (
          <div className="mt-6 space-y-3">
            <h3 className="text-sm font-medium text-muted-foreground uppercase tracking-wide">{t('profile_header.social_media')}</h3>
            {(profile?.website || profile?.linkedin_url || profile?.github_url || profile?.twitter_url) ? (
              <div className="flex items-center gap-3">
                {profile?.website && (
                  <a 
                    href={profile.website} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 text-sm text-muted-foreground hover:text-primary transition-colors"
                  >
                    <Globe className="w-4 h-4" />
                    <span>{t('profile_header.website')}</span>
                  </a>
                )}
                {profile?.linkedin_url && (
                  <a 
                    href={profile.linkedin_url} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 text-sm text-muted-foreground hover:text-primary transition-colors"
                  >
                    <Linkedin className="w-4 h-4" />
                    <span>{t('profile_header.linkedin')}</span>
                  </a>
                )}
                {profile?.github_url && (
                  <a 
                    href={profile.github_url} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 text-sm text-muted-foreground hover:text-primary transition-colors"
                  >
                    <Github className="w-4 h-4" />
                    <span>{t('profile_header.github')}</span>
                  </a>
                )}
                {profile?.twitter_url && (
                  <a 
                    href={profile.twitter_url} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 text-sm text-muted-foreground hover:text-primary transition-colors"
                  >
                    <Twitter className="w-4 h-4" />
                    <span>{t('profile_header.twitter')}</span>
                  </a>
                )}
              </div>
            ) : (
              <p className="text-muted-foreground/60 italic text-sm">
                {t('profile_header.no_social_media')}
              </p>
            )}
          </div>
        )}
        </div>
      </div>

      <ImageCropDialog
        isOpen={cropDialogOpen}
        onClose={() => {
          setCropDialogOpen(false);
          if (selectedImageSrc) {
            URL.revokeObjectURL(selectedImageSrc);
            setSelectedImageSrc(null);
          }
        }}
        imageSrc={selectedImageSrc || ''}
        onCropComplete={handleCroppedImageUpload}
        cropShape="round"
        title={t('image_crop.title_avatar')}
      />
    </Card>
  );
};